import type { FilterValue } from "antd/es/table/interface";

/**
 * Helpers that turn the parent's filter state into the `filteredValue` shape
 * AntD emits for each column header filter.
 *
 * AntD table filtering is all-or-nothing: as soon as one column has a
 * `filteredValue`, every other column must be controlled too, otherwise AntD
 * drops the previously-set (uncontrolled) filters whenever a new one is applied
 * (SMT-1163). Keeping the exact value shapes here — and covering them with unit
 * tests — is the riskiest part of controlling all columns (numbers vs booleans
 * vs strings), so it lives in one place.
 */

/**
 * Invoice/receipt column: AntD emits `[1]` for "has receipt", `[0]` for
 * "no receipt". The parent stores this as a boolean (or undefined when unset).
 */
export const deriveInvoiceFilteredValue = (
  assetsFilter: boolean | undefined
): FilterValue | null => {
  if (assetsFilter === undefined) {
    return null;
  }
  return assetsFilter ? [1] : [0];
};

/**
 * Note column: single-select options are `0` (internal note), `1` (user's
 * note) and `2` (all). The parent stores two independent booleans, so "both
 * set" maps back to the "All" option.
 */
export const deriveNoteFilteredValue = (noteFilters: {
  internalNote: boolean | null;
  personalNote: boolean | null;
}): FilterValue | null => {
  const hasInternal = !!noteFilters.internalNote;
  const hasPersonal = !!noteFilters.personalNote;

  if (hasInternal && hasPersonal) {
    return [2];
  }
  if (hasInternal) {
    return [0];
  }
  if (hasPersonal) {
    return [1];
  }
  return null;
};

/**
 * Verified column: options are `true` (verified), `false` (not verified) and
 * `"ml"` (verified by ML). "Verified by ML" is tracked in a separate boolean in
 * the parent and takes precedence over the plain verified flag.
 */
export const deriveVerifiedFilteredValue = (
  verifiedFilter: boolean | undefined,
  verifiedByMLFilter: boolean | undefined
): FilterValue | null => {
  if (verifiedByMLFilter) {
    return ["ml"];
  }
  if (verifiedFilter === undefined) {
    return null;
  }
  return [verifiedFilter];
};

/**
 * Submitted column: the option value is the submission-state string itself. The
 * parent stores it as a string (or null when unset).
 */
export const deriveSubmittedFilteredValue = (
  submissionStatusFilter: string | null
): FilterValue | null => {
  if (!submissionStatusFilter) {
    return null;
  }
  return [submissionStatusFilter];
};
